<!-- A Date and Time picker designed for use on the Journey Planner. The prop pickerType must be fed in to tell the component which variable to bind to in the Journey Store. Use v-if to display this component. A 'close' event is emitted when the planner should be closed. -->
<script setup lang="ts">
import { useJourneyStore } from "@/stores/journey-store";
import { JourneyTimeType } from "@/models/JourneyTimeType";
import { DateTime } from "luxon";

const emit = defineEmits(["close"]);
const journeyStore = useJourneyStore();
const props = defineProps({
    pickerType: {
        type: String,
        required: true,
        validator: (value: string) => ["outbound", "return"].includes(value),
    },
});
const minutes = [{ title: "00", value: 0 }, 15, 30, 45];
const hours = [{ title: "00", value: 0 }, { title: "01", value: 1 }, { title: "02", value: 2 }, { title: "03", value: 3 }, { title: "04", value: 4 }, { title: "05", value: 5 }, { title: "06", value: 6 }, { title: "07", value: 7 }, { title: "08", value: 8 }, { title: "09", value: 9 }, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

function handleTimeTypeChange(value: JourneyTimeType) {
    if (props.pickerType === "outbound") {
        journeyStore.query.outboundTimeType = value;
    } else {
        journeyStore.query.returnTimeType = value;
    }
}

function getTimeType() {
    if (props.pickerType === "outbound") {
        return journeyStore.query.outboundTimeType;
    }
    return journeyStore.query.returnTimeType;
}

function handleHourChange(value: number) {
    if (props.pickerType === "outbound") {
        journeyStore.query.outboundDate = journeyStore.query.outboundDate.set({ hour: value });
    } else {
        journeyStore.query.returnDate = journeyStore.query.returnDate.set({ hour: value });
    }
}

function getHour() {
    if (props.pickerType === "outbound") {
        return journeyStore.query.outboundDate.get("hour");
    }
    return journeyStore.query.returnDate.get("hour");
}

function handleMinuteChange(value: number) {
    if (props.pickerType === "outbound") {
        journeyStore.query.outboundDate = journeyStore.query.outboundDate.set({ minute: value });
    } else {
        journeyStore.query.returnDate = journeyStore.query.returnDate.set({ minute: value });
    }
}

function getMinute() {
    if (props.pickerType === "outbound") {
        return journeyStore.query.outboundDate.get("minute");
    }
    return journeyStore.query.returnDate.get("minute");
}

function getYesterdaysDate() {
    return DateTime.now().setZone("Europe/London").minus({ days: 1 });
}

// The API only allows a date range of 90 days, so we need to set the max date to 90 days from now.
function getMaxDate() {
    return DateTime.now().setZone("Europe/London").plus({ days: 90 });
}

// Set the date in the store to the date selected by the user. Need to add the time in though as the var in the store is both date and time.
function handleDateChange(date: DateTime) {
    let dateTime = date;

    if (props.pickerType === "outbound") {
        dateTime = dateTime.set({ hour: journeyStore.query.outboundDate.get("hour"), minute: journeyStore.query.outboundDate.get("minute") });
        journeyStore.query.outboundDate = dateTime;
    } else {
        dateTime = dateTime.set({ hour: journeyStore.query.returnDate.get("hour"), minute: journeyStore.query.returnDate.get("minute") });
        journeyStore.query.returnDate = dateTime;
    }
}

function getDate() {
    if (props.pickerType === "outbound") {
        return journeyStore.query.outboundDate;
    }
    return journeyStore.query.returnDate;
}

function getMinimumDateForCalendar() {
    if (props.pickerType === "outbound") {
        return getYesterdaysDate();
    }
    return journeyStore.query.outboundDate.minus({ days: 1 });
}
</script>

<template>
    <v-date-picker hide-header :min="getMinimumDateForCalendar()" :max="getMaxDate()" position="absolute" :model-value="getDate()" @update:model-value="handleDateChange" elevation="0">
        <template v-slot:actions>
            <div class="d-flex flex-wrap">
                <div class="cal-date-picker">
                    <v-select
                        :items="[
                            { title: 'Leaving at', value: JourneyTimeType.DepartingAt },
                            { title: 'Arriving by', value: JourneyTimeType.ArrivingBefore },
                        ]"
                        @update:model-value="handleTimeTypeChange"
                        :model-value="getTimeType()"
                        density="compact"
                        hide-details
                        variant="outlined"
                        class="time-type-select"
                    ></v-select>
                    <v-select :items="hours" :model-value="getHour()" @update:model-value="handleHourChange" density="compact" hide-details variant="outlined"></v-select>
                    <v-select :items="minutes" :model-value="getMinute()" @update:model-value="handleMinuteChange" density="compact" hide-details variant="outlined"></v-select>
                </div>
                <v-btn @click="emit('close')" class="w-100 text-white mt-3" variant="flat" color="var(--vt-c-orange)">Done</v-btn>
            </div>
        </template>
    </v-date-picker>
</template>

<style scoped lang="scss">
.v-date-picker {
    height: 100%;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;

    :deep(.v-picker__body) {
        flex-basis: 100%;
    }
}

:deep(.v-picker__actions) {
    justify-content: start;
}

.cal-date-picker {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
}

.cal-date-picker .time-type-select {
    min-width: fit-content;
}

.cal-date-picker :deep(.v-field) {
    padding-right: 4px;
}

@media (max-width: 322px) {
    .cal-date-picker :deep(.v-field .v-field__input) {
        padding-left: 7px;
    }
}
</style>
